/*
            |
           / \
          / _ \
         |.o '.|
         |'._.'|
         |     |
       ,'|  |  |`.
      /  |  |  |  \
      |,-'--|--'-.|
bolean Animation Framework — Shopify Edition

Usage:
Push animation objects into window.boAnimsQueue from any template or snippet
BEFORE this script loads (i.e. earlier in the page).

Two animation types:

  'CSS'  — adds the animation class (e.g. 'fade-in-up') to each matched element
           individually as it enters the viewport. Elements are pre-hidden via an
           inline <style> injected by this script's <style> companion tag (see below).
           nth-child CSS delays handle stagger within a group.

  'STAGGER' — targets a PARENT selector. Each direct child of that parent is
           observed independently. When a child enters the viewport it fades+slides
           in. Children that are already in the viewport when the section is reached
           animate in sequence (via queued timeouts). Children that scroll in later
           animate individually as they appear. This replaces the old popInDelay.

Example:
  window.boAnimsQueue = window.boAnimsQueue || [];
  window.boAnimsQueue.push(
    // Each li observed individually, nth-child CSS delays handle stagger
    {
      selector: '.numbered-cards li',
      animation: 'fade-in-up',
      type: 'CSS',
      rootMargin: '0px 0px -10% 0px'
    },
    // Each direct child of ._content observed individually with JS stagger
    {
      selector: '#excellence ._content',
      animation: 'staggerChildren',
      type: 'STAGGER',
      delay: 120,
      rootMargin: '0px 0px -5% 0px'
    }
  );

rootMargin: shrinks the viewport from the bottom so animations only fire once
elements are well into the visible area. "0px 0px -20% 0px" = trigger when the
element is 20% above the bottom edge of the viewport.
*/

/* ─── FRAMEWORK ─────────────────────────────────────────────────────────── */

document.addEventListener('DOMContentLoaded', () => {

  const queue = window.boAnimsQueue;
  if (!Array.isArray(queue) || queue.length === 0) return;

  // Deduplicate: same selector pushed from multiple snippets on one page
  const seen = new Set();
  const animations = queue.filter((a) => {
    const key = a.selector + '|' + a.type;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Group by rootMargin to minimise observer count
  const byMargin = animations.reduce((acc, anim) => {
    const margin = anim.rootMargin || '0px 0px -20% 0px';
    (acc[margin] = acc[margin] || []).push(anim);
    return acc;
  }, {});

  Object.entries(byMargin).forEach(([rootMargin, group]) => {

    // ── CSS type: observe each matched element directly ──────────────────
    const cssAnims = group.filter((a) => a.type === 'CSS');
    if (cssAnims.length > 0) {
      const cssObserver = new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          cssAnims.forEach((anim) => {
            if (!entry.target.matches(anim.selector)) return;
            // Remove ba-hidden and add animation class in the same frame —
            // the keyframe's `from` state takes over immediately, no 1→0→1 flash.
            entry.target.classList.remove('ba-hidden');
            entry.target.classList.add(anim.animation);
            obs.unobserve(entry.target);
          });
        });
      }, { threshold: 0, rootMargin });

      cssAnims.forEach((anim) => {
        document.querySelectorAll(anim.selector).forEach((el) => cssObserver.observe(el));
      });
    }

    // ── STAGGER type: observe each child of the parent individually ──────
    // When a child enters the viewport it triggers its own transition.
    // Children already visible when the parent is reached animate via
    // queued timeouts so they appear in order rather than all at once.
    const staggerAnims = group.filter((a) => a.type === 'STAGGER');
    staggerAnims.forEach((anim) => {
      document.querySelectorAll(anim.selector).forEach((parent) => {
        const children = Array.from(parent.querySelectorAll(':scope > *'));
        const delay = anim.delay || 100;

        // Children are already hidden via .ba-hidden (applied before first paint).
        // Set the transition now so it's ready when revealChild fires.
        children.forEach((child) => {
          child.style.transition = 'opacity 0.55s ease-out, transform 0.55s ease-out';
          child.style.willChange = 'opacity, transform';
        });

        // Track how many children have already been queued/revealed,
        // so children that scroll in later get instant reveal (no extra delay)
        let revealedCount = 0;
        let batchPending = false;

        const childObserver = new IntersectionObserver((entries, obs) => {
          // Collect all children that just became visible in this callback batch
          const nowVisible = entries
            .filter((e) => e.isIntersecting)
            .map((e) => e.target);

          if (nowVisible.length === 0) return;

          if (!batchPending) {
            // First batch entering viewport: stagger them in order
            batchPending = true;
            const ordered = children.filter((c) => nowVisible.includes(c));
            ordered.forEach((child, i) => {
              const childIndex = children.indexOf(child);
              setTimeout(() => {
                revealChild(child);
                revealedCount++;
              }, i * delay);
              obs.unobserve(child);
            });
            // After stagger completes, allow late entries to animate immediately
            setTimeout(() => { batchPending = false; }, ordered.length * delay + 50);
          } else {
            // Late-scrolling children: animate immediately (no queued delay)
            nowVisible.forEach((child) => {
              revealChild(child);
              revealedCount++;
              obs.unobserve(child);
            });
          }
        }, { threshold: 0, rootMargin });

        children.forEach((child) => childObserver.observe(child));
      });
    });

  });
});

/* ─── HELPERS ────────────────────────────────────────────────────────────── */

function revealChild(el) {
  // Remove ba-hidden (which had opacity:0 with !important and no transition).
  // The inline transition is already set, so removing ba-hidden + inline opacity
  // in the same synchronous block lets the browser transition from 0 to 1 cleanly.
  el.classList.remove('ba-hidden');
  el.style.opacity = '';
  el.style.transform = '';
}

/**
 * Animates a number from 0 to its displayed value.
 * @param {HTMLElement} el        - Element whose textContent is the target number
 * @param {number}      duration  - Animation duration in ms (default 1200)
 */
function animateNumber(el, duration = 1200) {
  let start = null;
  const endValue = parseInt(el.textContent, 10);
  if (isNaN(endValue)) return;

  function step(timestamp) {
    if (!start) start = timestamp;
    const progress = timestamp - start;
    const current = Math.min(Math.floor((endValue * progress) / duration), endValue);
    el.textContent = current;
    if (progress < duration) {
      requestAnimationFrame(step);
    } else {
      el.textContent = endValue;
    }
  }
  requestAnimationFrame(step);
}
